import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiChevronDown, FiSearch, FiX } from 'react-icons/fi';

function normalizeOption(opt) {
  if (opt != null && typeof opt === 'object') {
    return {
      value: String(opt.value ?? ''),
      label: String(opt.label ?? opt.value ?? ''),
    };
  }
  const text = String(opt ?? '');
  return { value: text, label: text };
}

function SearchableSelect({
  options = [],
  value = '',
  onChange,
  placeholder = 'Search and select…',
  searchPlaceholder = 'Type to search…',
  disabled = false,
  error = false,
  placement = 'auto',
  id,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [dropUp, setDropUp] = useState(placement === 'top');
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const normalized = useMemo(() => options.map(normalizeOption), [options]);

  const selected = useMemo(
    () => normalized.find((opt) => opt.value === value) || null,
    [normalized, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return normalized;
    return normalized.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [normalized, query]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return undefined;
    }

    if (placement === 'top') {
      setDropUp(true);
    } else if (placement === 'bottom') {
      setDropUp(false);
    } else if (rootRef.current) {
      const rect = rootRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setDropUp(spaceBelow < 260 && spaceAbove > spaceBelow);
    }

    const onDoc = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, placement]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  const handleSelect = (opt) => {
    onChange?.(opt.value);
    setOpen(false);
    setQuery('');
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onChange?.('');
    setQuery('');
  };

  return (
    <div
      className={`searchable-select${open ? ' is-open' : ''}${error ? ' is-error' : ''}${disabled ? ' is-disabled' : ''}${dropUp ? ' is-drop-up' : ''}`}
      ref={rootRef}
    >
      <button
        type="button"
        id={id}
        className="searchable-select-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
      >
        <span className={selected ? 'searchable-select-value' : 'searchable-select-placeholder'}>
          {selected?.label || placeholder}
        </span>
        <span className="searchable-select-actions">
          {selected ? (
            <span
              className="searchable-select-clear"
              role="button"
              tabIndex={0}
              aria-label="Clear selection"
              onClick={handleClear}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') handleClear(e);
              }}
            >
              <FiX size={14} />
            </span>
          ) : null}
          <FiChevronDown size={16} className="searchable-select-chevron" />
        </span>
      </button>

      {open && (
        <div className="searchable-select-dropdown" role="listbox">
          <div className="searchable-select-search">
            <FiSearch size={14} aria-hidden />
            <input
              ref={inputRef}
              type="text"
              value={query}
              placeholder={searchPlaceholder}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setOpen(false);
                if (e.key === 'Enter' && filtered[0]) {
                  e.preventDefault();
                  handleSelect(filtered[0]);
                }
              }}
            />
          </div>
          <div className="searchable-select-options">
            {filtered.length === 0 ? (
              <div className="searchable-select-empty">No options found</div>
            ) : (
              filtered.slice(0, 200).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={opt.value === value}
                  className={`searchable-select-option${opt.value === value ? ' is-selected' : ''}`}
                  onClick={() => handleSelect(opt)}
                >
                  {opt.label}
                </button>
              ))
            )}
            {filtered.length > 200 && (
              <div className="searchable-select-empty">Keep typing to narrow results…</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
